import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { MDBContainer, MDBRow, MDBCol, MDBInput } from "mdb-react-ui-kit";
import { useMessageBox } from "../components/Common/MessageBox";
import { useAuth } from "../context/AuthContext";
import { normalizeRole } from "../utils/rolePermissions";
import ProductLogTimeline from "./ProductLogTimeline";
const API_HOST =
  process.env.REACT_APP_API_URL ||
  "https://crosslisting-backend-aacranbzdyd3ffbd.centralindia-01.azurewebsites.net";

const BASE_URL = API_HOST;

const safeParse = (val) => {
  if (!val) return null;
  if (typeof val === "object") return val;
  try {
    return JSON.parse(val);
  } catch {
    return val;
  }
};

const getHeadlinePrice = (pricing) => {
  if (!pricing) return null;
  if (
    pricing.price_recommended !== undefined &&
    pricing.price_recommended !== null
  ) {
    return pricing.price_recommended;
  }
  if (pricing.vendor_payout !== undefined && pricing.vendor_payout !== null) {
    return pricing.vendor_payout;
  }
  return null;
};

const normalizeKey = (k) => k.toLowerCase().replace(/[^a-z0-9]/g, "");

const stripFieldsPresentIn = (fields, referenceFields) => {
  const referenceKeys = new Set(Object.keys(referenceFields).map(normalizeKey));
  const out = {};
  Object.entries(fields).forEach(([k, v]) => {
    if (!referenceKeys.has(normalizeKey(k))) out[k] = v;
  });
  return out;
};

const buildMarketplaces = (product, ebay, shopify, vinted, poshmark, depop) => {
  const result = {};

  const displayPrice = getHeadlinePrice(product.pricing) ?? product.price ?? 0;

  result.local = {
    Title: product.title || "",
    Description: product.description || "",
    SKU: product.sku || "",
    Price: displayPrice,
    Quantity: product.quantity || 0,
    Category: product.category || "Other",
    Condition: product.condition || "",
  };

  if (ebay) {
    const raw = {};
    if (ebay.aspects) {
      const parsed = safeParse(ebay.aspects);
      if (parsed && typeof parsed === "object") {
        Object.entries(parsed).forEach(([k, v]) => {
          const val = Array.isArray(v) ? v.join(", ") : v;
          if (val && val !== "N/A") raw[k] = val;
        });
      }
    }
    if (ebay.currency) raw.Currency = ebay.currency;
    result.ebay = stripFieldsPresentIn(raw, result.local);
  }

  if (shopify) {
    const raw = {
      Vendor: shopify.vendor || "",
      "Product Type": shopify.product_type || "",
      Status: shopify.status || "",
    };
    result.shopify = stripFieldsPresentIn(raw, result.local);
  }

  if (vinted) {
    const raw = {
      Category: vinted.category || "",
      Brand: vinted.brand || "",
      Size: vinted.size || "",
      Condition: vinted.condition || "",
      "Package Size": vinted.package_size || "",
      Color: vinted.color || "",
      // Separate key from "Description" so it never gets stripped by the
      // General-Info dedupe — Vinted's description is a distinct field
      // (`vinted_description` on the backend), not the shared one.
      "Vinted Description": vinted.description || "",
    };
    result.vinted = stripFieldsPresentIn(raw, result.local);
  }

  if (poshmark) {
    const raw = {
      Category: poshmark.category || "",
      Size: poshmark.size || "",
      Brand: poshmark.brand || "",
      Color: poshmark.color || "",
      Condition: poshmark.condition || "",
      Availability: poshmark.availability || "",
    };
    result.poshmark = stripFieldsPresentIn(raw, result.local);
  }

  if (depop) {
    const raw = {
      Category: depop.category || "",
      Brand: depop.brand || "",
      Size: depop.size || "",
      Condition: depop.condition || "",
      Color: depop.color || "",
    };
    result.depop = stripFieldsPresentIn(raw, result.local);
  }

  return result;
};

const MARKETPLACE_ACCENT = {
  local: "#475569",
  ebay: "#c2384a",
  shopify: "#5a9c3f",
  vinted: "#0b9aa3",
  poshmark: "#7b2d8b",
  depop: "#c23b1f",
};

const MARKETPLACE_LABELS = {
  local: "General Info",
  ebay: "eBay",
  shopify: "Shopify",
  vinted: "Vinted",
  poshmark: "Poshmark",
  depop: "Depop",
};

// Only these fields are actually persisted by POST /api/update-products/update-platform-details.
// Everything else in a marketplace tab is display-only until the backend supports it.
const SAVABLE_LOCAL_KEYS = ["Title", "Description", "Price"];

const dedupeImagesKeepOrder = (urls) => {
  const seen = new Set();
  return urls.filter((url) => {
    if (!url || seen.has(url)) return false;
    seen.add(url);
    return true;
  });
};

const ProductDetailsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { showError, MessageBoxComponent } = useMessageBox();
  const { user } = useAuth();
  const userRole = normalizeRole(user?.role);

  // NOTE: confirm this against what normalizeRole() actually returns for a
  // vendor account — the backend gates on role === 'seller' at the DB level.
  const isVendor = userRole === "vendor" || userRole === "seller";
  const isSeller = userRole === "seller";

  const [marketplaces, setMarketplaces] = useState(null);
  const [originalMarketplaces, setOriginalMarketplaces] = useState(null);
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("local");
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState("");
  const [platformActionLoading, setPlatformActionLoading] = useState(null);

  // ── Logs ──
  const [logs, setLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(true);

  const fetchLogs = async () => {
    try {
      setLogsLoading(true);
      const token = localStorage.getItem("token");
      const res = await fetch(`${BASE_URL}/api/update-products/logs/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const json = await res.json();
      if (json.success) setLogs(json.logs || []);
    } catch (err) {
      console.error("Failed to load logs", err);
    } finally {
      setLogsLoading(false);
    }
  };

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);

        const token = localStorage.getItem("token");
        const res = await fetch(
          `${BASE_URL}/api/product-full/${id}?role=${encodeURIComponent(
            userRole || "",
          )}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );
        const json = await res.json();

        if (!json.success || !json.data) {
          showError("Product not found");
          return;
        }

        const { product, ebay, shopify, vinted, poshmark, depop } = json.data;
        const mp = buildMarketplaces(
          product,
          ebay,
          shopify,
          vinted,
          poshmark,
          depop,
        );
        setMarketplaces(mp);
        setOriginalMarketplaces(JSON.parse(JSON.stringify(mp)));
        setImages(dedupeImagesKeepOrder(product.images || []));
        setActiveTab(Object.keys(mp)[0] || "local");
      } catch (err) {
        console.error(err);
        showError("Failed to load product");
      } finally {
        setLoading(false);
      }
    };

    load();
    if (!isSeller) fetchLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, userRole]);

  const handleFieldChange = (market, key, value) => {
    setMarketplaces((prev) => ({
      ...prev,
      [market]: { ...prev[market], [key]: value },
    }));
  };

  // Diff current vs. original and build the `changes` object the backend expects.
  const buildChanges = () => {
    const changes = {};
    if (!marketplaces?.local || !originalMarketplaces?.local) return changes;

    if (marketplaces.local.Title !== originalMarketplaces.local.Title) {
      changes.title = marketplaces.local.Title;
    }
    if (
      marketplaces.local.Description !== originalMarketplaces.local.Description
    ) {
      changes.description = marketplaces.local.Description;
    }
    if (
      String(marketplaces.local.Price) !==
      String(originalMarketplaces.local.Price)
    ) {
      const num = Number(marketplaces.local.Price);
      if (!Number.isNaN(num)) changes.price = num;
    }

    const vintedDesc = marketplaces.vinted?.["Vinted Description"];
    const originalVintedDesc =
      originalMarketplaces.vinted?.["Vinted Description"];
    if (vintedDesc !== undefined && vintedDesc !== originalVintedDesc) {
      changes.vinted_description = vintedDesc;
    }

    return changes;
  };

  const hasChanges = Object.keys(buildChanges()).length > 0;

  const availableTabs = marketplaces ? Object.keys(marketplaces) : [];
  const platformTabs = availableTabs.filter((t) => t !== "local");

  const handleSave = async () => {
    const changes = buildChanges();
    if (Object.keys(changes).length === 0) {
      setSaveMessage("No changes to save.");
      return;
    }

    setSaving(true);
    setSaveMessage("");
    try {
      const token = localStorage.getItem("token");
      const payload = {
        product_id: id,
        user_id: user?.id,
        company_id: user?.company_id,
        changes,
      };

      // Vendors get their listed platforms auto-resolved server-side; everyone
      // else must explicitly say which platform tabs to push the change to.
      if (!isVendor) {
        payload.platforms = platformTabs;
      }

      const res = await fetch(
        `${BASE_URL}/api/update-products/update-platform-details`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(payload),
        },
      );
      const json = await res.json();

      if (!res.ok || !json.success) {
        throw new Error(json.message || "Failed to save changes");
      }

      setOriginalMarketplaces(JSON.parse(JSON.stringify(marketplaces)));
      setSaveMessage("Changes saved.");
      fetchLogs(); // refresh the log timeline with the change we just made
    } catch (err) {
      console.error(err);
      showError(err.message || "Failed to save changes");
    } finally {
      setSaving(false);
    }
  };

  const handleListDelist = async (platform, action) => {
    setPlatformActionLoading(`${platform}:${action}`);
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(
        `${BASE_URL}/api/update-products/update-platform-details`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            product_id: id,
            user_id: user?.id,
            company_id: user?.company_id,
            platforms: [platform],
            changes: { [action]: true },
          }),
        },
      );
      const json = await res.json();
      if (!res.ok || !json.success) {
        throw new Error(json.message || `Failed to ${action} on ${platform}`);
      }
      setSaveMessage(
        `${MARKETPLACE_LABELS[platform] || platform} ${action}ed.`,
      );
      fetchLogs();
    } catch (err) {
      console.error(err);
      showError(err.message || `Failed to ${action}`);
    } finally {
      setPlatformActionLoading(null);
    }
  };

  if (loading) {
    return (
      <div
        className="d-flex flex-column align-items-center justify-content-center"
        style={{ minHeight: "60vh" }}
      >
        <div
          className="spinner-border"
          role="status"
          style={{ color: "#475569", width: 40, height: 40 }}
        />
        <p className="mt-3" style={{ color: "#64748b", fontSize: 14 }}>
          Loading product details…
        </p>
      </div>
    );
  }

  if (!marketplaces) {
    return (
      <div
        className="d-flex flex-column align-items-center justify-content-center"
        style={{ minHeight: "60vh" }}
      >
        <p style={{ color: "#64748b" }}>No product data found.</p>
        <button
          onClick={() => navigate(-1)}
          style={{
            marginTop: 12,
            padding: "8px 18px",
            borderRadius: 8,
            border: "1px solid #e2e8f0",
            background: "#fff",
            color: "#334155",
            fontWeight: 600,
            fontSize: 14,
            cursor: "pointer",
          }}
        >
          Go back
        </button>
      </div>
    );
  }

  return (
    <MDBContainer className="py-4" style={{ maxWidth: 980 }}>
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-2">
        <div>
          <p
            style={{
              margin: 0,
              fontSize: 12,
              fontWeight: 600,
              letterSpacing: "0.06em",
              textTransform: "uppercase",
              color: "#94a3b8",
            }}
          >
            Product
          </p>
          <h3
            className="fw-bold mb-0"
            style={{ color: "#0f172a", fontSize: 22 }}
          >
            {marketplaces.local?.Title || "Product Details"}
          </h3>
        </div>
        <div className="d-flex gap-2">
          {!isSeller && (
            <button
              onClick={handleSave}
              disabled={!hasChanges || saving}
              style={{
                padding: "9px 16px",
                borderRadius: 8,
                border: "none",
                background: hasChanges && !saving ? "#0f172a" : "#cbd5e1",
                color: "#fff",
                fontWeight: 600,
                fontSize: 14,
                cursor: hasChanges && !saving ? "pointer" : "not-allowed",
              }}
            >
              {saving ? "Saving…" : "Save changes"}
            </button>
          )}
          <button
            onClick={() => navigate(-1)}
            style={{
              padding: "9px 16px",
              borderRadius: 8,
              border: "1px solid #e2e8f0",
              background: "#fff",
              color: "#334155",
              fontWeight: 600,
              fontSize: 14,
              cursor: "pointer",
              transition: "background 0.15s ease",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.background = "#f8fafc")}
            onMouseLeave={(e) => (e.currentTarget.style.background = "#fff")}
          >
            ← Back
          </button>
        </div>
      </div>

      {saveMessage && (
        <p style={{ color: "#16a34a", fontSize: 13, marginBottom: 12 }}>
          {saveMessage}
        </p>
      )}

      {/* Images */}
      {images.length > 0 && (
        <div className="mb-4 d-flex gap-2 flex-wrap">
          {images.map((img, i) => (
            <img
              key={img}
              src={img}
              alt={`Product ${i + 1}`}
              style={{
                width: 96,
                height: 96,
                objectFit: "cover",
                borderRadius: 10,
                border: "1px solid #e2e8f0",
                boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)",
              }}
            />
          ))}
        </div>
      )}

      {/* Tabs — segmented control */}
      <div
        className="d-flex flex-wrap mb-4"
        style={{
          background: "#f1f5f9",
          borderRadius: 10,
          padding: 4,
          gap: 4,
        }}
      >
        {availableTabs.map((tab) => {
          const isActive = activeTab === tab;
          const accent = MARKETPLACE_ACCENT[tab] || "#475569";
          return (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              style={{
                padding: "8px 16px",
                borderRadius: 7,
                border: "none",
                background: isActive ? "#fff" : "transparent",
                color: isActive ? accent : "#64748b",
                fontWeight: 600,
                fontSize: 13.5,
                cursor: "pointer",
                boxShadow: isActive
                  ? "0 1px 3px rgba(15, 23, 42, 0.10)"
                  : "none",
                transition: "all 0.15s ease",
              }}
            >
              {MARKETPLACE_LABELS[tab] || tab}
            </button>
          );
        })}
      </div>

      {/* Active Tab Fields */}
      {availableTabs.map((market) => {
        const fields = Object.entries(marketplaces[market] || {});
        const canEditField = (key) =>
          market === "local"
            ? SAVABLE_LOCAL_KEYS.includes(key)
            : market === "vinted" && key === "Vinted Description";

        return (
          <div
            key={market}
            style={{ display: activeTab === market ? "block" : "none" }}
          >
            <div
              style={{
                background: "#fff",
                border: "1px solid #e2e8f0",
                borderRadius: 12,
                padding: "24px 28px",
                boxShadow: "0 1px 2px rgba(15, 23, 42, 0.03)",
              }}
            >
              <div className="d-flex align-items-center gap-2 mb-4">
                <span
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    background: MARKETPLACE_ACCENT[market] || "#475569",
                    display: "inline-block",
                  }}
                />
                <h6
                  className="mb-0 fw-bold"
                  style={{ color: "#0f172a", fontSize: 15 }}
                >
                  {MARKETPLACE_LABELS[market] || market}
                </h6>
                <span style={{ color: "#94a3b8", fontSize: 12.5 }}>
                  {fields.length} field{fields.length === 1 ? "" : "s"}
                </span>

                {/* List / Delist — non-vendor only, mirrors the backend's own role check */}
                {market !== "local" && !isVendor && (
                  <div className="d-flex gap-2 ms-auto">
                    <button
                      onClick={() => handleListDelist(market, "list")}
                      disabled={platformActionLoading === `${market}:list`}
                      style={{
                        padding: "5px 12px",
                        borderRadius: 6,
                        border: "1px solid #bbf7d0",
                        background: "#f0fdf4",
                        color: "#15803d",
                        fontWeight: 600,
                        fontSize: 12.5,
                        cursor: "pointer",
                      }}
                    >
                      {platformActionLoading === `${market}:list`
                        ? "…"
                        : "List"}
                    </button>
                    <button
                      onClick={() => handleListDelist(market, "delist")}
                      disabled={platformActionLoading === `${market}:delist`}
                      style={{
                        padding: "5px 12px",
                        borderRadius: 6,
                        border: "1px solid #fecaca",
                        background: "#fef2f2",
                        color: "#b91c1c",
                        fontWeight: 600,
                        fontSize: 12.5,
                        cursor: "pointer",
                      }}
                    >
                      {platformActionLoading === `${market}:delist`
                        ? "…"
                        : "Delist"}
                    </button>
                  </div>
                )}
              </div>

              {fields.length === 0 ? (
                <p style={{ color: "#94a3b8", fontSize: 14, margin: 0 }}>
                  No additional details for this marketplace.
                </p>
              ) : (
                <MDBRow>
                  {fields.map(([key, value]) => {
                    const editable = canEditField(key);
                    return (
                      <MDBCol md="6" lg="4" className="mb-3" key={key}>
                        <div>
                          <label
                            style={{
                              display: "block",
                              fontSize: 11,
                              fontWeight: 700,
                              letterSpacing: "0.04em",
                              textTransform: "uppercase",
                              color: "#94a3b8",
                              marginBottom: 4,
                            }}
                          >
                            {key}
                          </label>
                          <MDBInput
                            value={String(value ?? "")}
                            disabled={!editable}
                            onChange={(e) =>
                              editable &&
                              handleFieldChange(market, key, e.target.value)
                            }
                            style={{
                              fontSize: 14,
                              opacity: editable ? 1 : 0.6,
                            }}
                          />
                        </div>
                      </MDBCol>
                    );
                  })}
                </MDBRow>
              )}
            </div>
          </div>
        );
      })}

      {/* Activity + Log Summary — hidden for sellers */}
      {!isSeller && (
        <div className="mt-4">
          <h6
            className="fw-bold mb-3"
            style={{ color: "#0f172a", fontSize: 15 }}
          >
            Activity
          </h6>
          {logsLoading ? (
            <p style={{ color: "#94a3b8", fontSize: 14 }}>Loading activity…</p>
          ) : (
            <ProductLogTimeline logs={logs} />
          )}
        </div>
      )}

      <MessageBoxComponent />
    </MDBContainer>
  );
};

export default ProductDetailsPage;
